// @flow
import * as React from 'react';
import { compose } from 'redux';
import { withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import DeliveryInfo from '@dpdgroupuk/fmx-ui/components/DeliveryInfo';
import { CollectionOptionIcon } from '@dpdgroupuk/fmx-ui/components/OptionIcon';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import Button from '@dpdgroupuk/fmx-ui/components/Button';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes';
import { withTrackProps } from '@dpdgroupuk/react-event-tracker';
import { formatOrdinalNumber } from '@dpdgroupuk/fmx-ui/utils/string';
import { getAddressWithPostcode } from '@dpdgroupuk/fmx-ui/utils/address';
import { fullDateValue } from '@dpdgroupuk/fmx-ui/utils/date';

import type { AuthorizedCollection } from '../../../../types/Collection';
import {
  getCollectionOptionHeaderControlActions,
  getCompletedOptionSpeedDealControlActions,
} from '../../../controlActions';
import {
  CHANGE_COLLECTION_DAY,
  TRACK_ANOTHER_COLLECTION,
  YOUR_COLLECTION_NUMBER,
} from '../../../../constants/message';
import { trackAnotherCollection } from '../../../historyActions';
import {
  CONFIRMATION_SCREEN,
  trackable,
} from '../../../../constants/analytics';

const useStyles = makeStyles(({ typography, breakpoints }) => ({
  button: {
    margin: `${typography.pxToRem(50)} 0 ${typography.pxToRem(20)}`,
    height: typography.pxToRem(36),
    width: typography.pxToRem(260),
    fontSize: typography.pxToRem(13.5),
    letterSpacing: typography.pxToRem(0.9),
    [breakpoints.down('sm')]: {
      marginTop: typography.pxToRem(30),
    },
  },
  collectionNumber: {
    opacity: 0.4,
    fontSize: typography.pxToRem(18),
    fontWeight: 300,
    [breakpoints.down('sm')]: {
      fontSize: typography.pxToRem(15),
    },
  },
}));

type Props = {
  actionCode?: string,
  data?: Object,
  collection: AuthorizedCollection,
  trackAnotherCollection: Function,
};

const CompleteStepComponent = ({
  actionCode,
  data = { address: '', day: '' },
  collection,
  trackAnotherCollection,
}: Props) => {
  const styles = useStyles();
  const { day } = data;
  const { year, month, dayOfWeek, dayNumber } = fullDateValue(day);
  const title = `As requested, your parcel will now be collected on ${dayOfWeek} the ${formatOrdinalNumber(
    dayNumber
  )} ${month} ${year} from`;
  const subTitle = collection
    ? getAddressWithPostcode(collection.collectionDetails.address)
    : '';

  return (
    <>
      <DeliveryInfo
        actionCode={actionCode}
        title={title}
        subTitle={subTitle}
        getOptionIcon={() => CollectionOptionIcon}
      />
      <Typography className={styles.collectionNumber}>
        {`${YOUR_COLLECTION_NUMBER}: ${collection.collectionNumber}`}
      </Typography>
      <Button className={styles.button} onClick={trackAnotherCollection}>
        {TRACK_ANOTHER_COLLECTION}
      </Button>
    </>
  );
};

const CompleteStep = compose(
  withRouter,
  withProps(props => ({
    trackAnotherCollection: () => trackAnotherCollection(props),
  })),
  withPageControl({
    title: CHANGE_COLLECTION_DAY,
    backgroundImage: IMAGES.MAP,
    trackable: trackable(CONFIRMATION_SCREEN),
    headerMenuActions: getCollectionOptionHeaderControlActions,
    speedDealActions: getCompletedOptionSpeedDealControlActions,
  }),
  withTrackProps({
    trackAnotherCollection: CONFIRMATION_SCREEN.TRACK_ANOTHER_COLLECTION,
  })
)(CompleteStepComponent);

export default CompleteStep;
